/**
 * S4 — the item sheet. An **overlay, not a route** (§ 3): it is one item's detail
 * over the count a manager is in the middle of, and pushing history for it would
 * make the back gesture mean "close the sheet" sometimes and "leave the count"
 * other times.
 *
 * It holds exactly four things, and one of them is the point:
 *
 * **Clear this count** — the one action in the entire app that sends
 * `counted_qty: null`, deleting the staging line. It lives here, behind a
 * confirmation, precisely so that clearing can never be a side effect of leaving a
 * field (the ruling in `entryModel.blurOutcome`). The confirmation says what
 * clearing *means* rather than asking "are you sure": **"leave this item's
 * inventory unchanged"** is the actual consequence, and it is the sentence rule 5
 * is written in.
 *
 * **No on-hand figure on this sheet** — D-1, ruled 2026-08-19. The count is blind
 * by design, and this sheet is the place it would be most tempting to leak: it is
 * the "tell me about this item" screen. It cannot, in fact — the count list payload
 * carries no on-hand — but the intent is stated so nobody adds a second fetch to
 * supply one. (The 2026-10-30 reveal lives on the entry ROW, after the line saves,
 * and the server refuses it before then; this sheet does not host it.)
 */
import { useEffect, useRef, useState } from "react";

import type { CountLine, CountListItem } from "../api/types";
import { formatTimestamp } from "../sessions/format";
import { isZeroQuantity } from "./entryModel";

export interface ItemSheetProps {
  item: CountListItem;
  staged: CountLine | undefined;
  /** False for a session the server would refuse changes to. */
  editable: boolean;
  busy: boolean;
  onClear: () => void;
  onClose: () => void;
}

export function ItemSheet({ item, staged, editable, busy, onClear, onClose }: ItemSheetProps) {
  const [confirming, setConfirming] = useState(false);
  const panel = useRef<HTMLDivElement>(null);

  // Escape closes it, and focus moves into the panel when it opens. An overlay
  // that traps neither is unusable with an iPad keyboard attached, which is how
  // some of these counts are actually entered.
  useEffect(() => {
    panel.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const counted = staged !== undefined;

  return (
    <div className="sheet-backdrop" onClick={onClose} data-testid="item-sheet-backdrop">
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={`${item.item_no} details`}
        tabIndex={-1}
        ref={panel}
        onClick={(event) => event.stopPropagation()}
        data-testid="item-sheet"
      >
        <header className="sheet__header">
          <div>
            <div className="sheet__item-no">{item.item_no}</div>
            <div className="sheet__description">{item.description ?? "No description in BC"}</div>
          </div>
          <button type="button" className="ghost" onClick={onClose} aria-label="Close">
            Close
          </button>
        </header>

        <dl className="facts">
          <div>
            <dt>Unit of measure</dt>
            {/* Shown, never entered: staging defaults a blank UOM from the item
                card and that is proven on the wire, so the app does not
                second-guess it. */}
            <dd>{item.base_unit_of_measure ?? <span className="muted">from the item card</span>}</dd>
          </div>
          <div>
            <dt>Section</dt>
            <dd>{item.section ?? <span className="muted">none in BC</span>}</dd>
          </div>
          <div>
            <dt>Vendor</dt>
            {/* BC's string verbatim, casing included (item 8). This is the one
                place a manager can check *why* an item landed in the vendor
                bucket it did. */}
            <dd>{item.vendor_name ?? <span className="muted">none in BC</span>}</dd>
          </div>
          <div>
            <dt>Lot tracked</dt>
            <dd>
              {item.lot_tracked === true && "Yes"}
              {item.lot_tracked === false && "No"}
              {item.lot_tracked == null && <span className="muted">unknown</span>}
            </dd>
          </div>
          <div>
            <dt>This count</dt>
            <dd>
              {counted ? (
                isZeroQuantity(staged!.counted_qty) ? (
                  <strong>0 — counted as none</strong>
                ) : (
                  <strong>
                    {staged!.counted_qty} {staged!.unit_of_measure_code ?? ""}
                  </strong>
                )
              ) : (
                <span className="muted">not counted</span>
              )}
            </dd>
          </div>
        </dl>

        {item.lot_tracked === true && (
          <p className="muted small" data-testid="lot-note">
            This item is lot-tracked in Business Central. <strong>Enter one total
            anyway</strong> — the same as every other item. The backend works out which
            lot an adjustment lands against; there is nothing to choose here.
          </p>
        )}

        {item.blocked === true && (
          <p className="notice small" data-testid="blocked-note">
            <strong>This item is blocked in Business Central.</strong> Counting it is
            fine, but the posting codeunit refuses a blocked item, so this count will
            not post until someone unblocks the item card or the line is cleared.
          </p>
        )}

        {counted && (
          <dl className="facts">
            <div>
              <dt>Entered by</dt>
              <dd>{staged!.entered_by ?? <span className="muted">—</span>}</dd>
            </div>
            <div>
              <dt>Entered at</dt>
              <dd>{formatTimestamp(staged!.entered_at)}</dd>
            </div>
          </dl>
        )}

        <div className="sheet__actions">
          {!counted && (
            <p className="muted small">
              Nothing to clear — this item has no count in Business Central's staging,
              which means its inventory is left exactly as it is.
            </p>
          )}

          {counted && editable && !confirming && (
            <button
              type="button"
              className="danger"
              onClick={() => setConfirming(true)}
              data-testid="clear-count"
            >
              Clear this count
            </button>
          )}

          {counted && editable && confirming && (
            <div className="confirm" data-testid="clear-confirm">
              {/* The consequence, in words, not "are you sure". */}
              <p>
                <strong>Leave this item's inventory unchanged?</strong>
              </p>
              <p className="small">
                This deletes the staged count for {item.item_no}. It does not count it
                as zero — Business Central will not adjust this item at all. To count it
                as none, type <code>0</code> instead.
              </p>
              <div className="confirm__buttons">
                <button
                  type="button"
                  className="danger"
                  onClick={onClear}
                  disabled={busy}
                  data-testid="clear-confirm-yes"
                >
                  {busy ? "Clearing…" : "Yes, leave it unchanged"}
                </button>
                <button type="button" className="ghost" onClick={() => setConfirming(false)}>
                  Keep the count
                </button>
              </div>
            </div>
          )}

          {counted && !editable && (
            <p className="muted small">
              Staging has frozen this session, so this count cannot be changed or
              cleared.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
